import { useState, useEffect } from 'react';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import { FileSelectDialog } from '../../../common/SharedComponents';
import { v4 as uuidv4 } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCancel, faDownload } from '@fortawesome/free-solid-svg-icons';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { LoanPropertyFileType } from '@apply/constants';

export const LoanFilesGrid = ({ files, fileTypeOptions, title = 'Files', onChange }) => {
    const [sortedFileTypeOptions, set_sortedFileTypeOptions] = useState([]);
    const [fileType, set_fileType] = useState(-1);
    const fileUploadOptions = {
        isOpen: false,
        maxFiles: 5,
        isReplace: false,
        typeName: '',
    };
    const [fileSelectOptions, set_fileSelectOptions] = useState(fileUploadOptions);

    useEffect(() => {
        if (!fileTypeOptions || fileTypeOptions.length === 0) {
            return;
        }
        const sortedOptions = fileTypeOptions.sort((a, b) => stringSorter(a.name, b.name));
        set_sortedFileTypeOptions(sortedOptions);
        set_fileType(sortedOptions[0].id);
    }, [fileTypeOptions]);

    useEffect(() => {
        if (!files || files.length === 0) {
            return;
        }
        files.forEach((f) => {
            f.guid = uuidv4();
        });
    }, [files]);

    const onDownload = async (e, url) => {
        e.preventDefault();
        e.stopPropagation();

        window.location.href = url;
    };

    const onFileTypeRender = (e) => {
        return fileTypeOptions.find((o) => o.id === e.value).name;
    };

    const onFileDelete = (e, guid) => {
        e.preventDefault();
        e.stopPropagation();

        const index = files.findIndex((f) => f.guid === guid);
        if (index === -1) {
            return;
        }

        const f = files[index];
        if (f.id === 0) {
            //new unsaved file
            const newFiles = [...files];
            newFiles.splice(index, 1);
            onChange && onChange(newFiles);
        } else {
            //Existing saved file - mark for deletion
            f.isDeleted = true;
            const newFiles = [...files];
            onChange && onChange(newFiles);
        }
    };

    const onFileUnDelete = (e, guid) => {
        e.preventDefault();
        e.stopPropagation();

        const index = files.findIndex((f) => f.guid === guid);
        if (index === -1) {
            return;
        }

        const f = files[index];
        if (f.id > 0) {
            f.isDeleted = false;
            const newFiles = [...files];
            onChange && onChange(newFiles);
        }
    };

    const onStatusRender = (e) => {
        if (e.data.id === 0) {
            return <span style={{ color: 'orange' }}>Awaiting Save</span>;
        } else if (e.data.isDeleted) {
            return <span style={{ color: 'darkred' }}>To be deleted</span>;
        } else {
            return <span style={{ color: 'darkgreen' }}>Saved</span>;
        }
    };

    const onDownloadRender = (e) => {
        if (!e.value) {
            return null;
        }
        return (
            <button
                type='button'
                onClick={(event) => onDownload(event, e.value)}
                className='button hollow'
                title='Download File'
                style={{ margin: '0' }}
            >
                <FontAwesomeIcon icon={faDownload} />
            </button>
        );
    };

    const onDeleteRender = (e) => {
        if (e.data.isDeleted) {
            return (
                <button
                    type='button'
                    onClick={(event) => onFileUnDelete(event, e.data.guid)}
                    className='button hollow'
                    title='Cancel Delete'
                    style={{ margin: '0' }}
                >
                    <FontAwesomeIcon icon={faCancel} />
                </button>
            );
        } else {
            return (
                <button
                    type='button'
                    onClick={(event) => onFileDelete(event, e.data.guid)}
                    className='button hollow'
                    title='Remove File'
                    style={{ margin: '0' }}
                    disabled={!e.data.canDelete}
                >
                    <FontAwesomeIcon icon={faTrashCan} />
                </button>
            );
        }
    };

    const onFileSelectSubmit = (id, selectedFiles) => {
        if (!selectedFiles || selectedFiles.length === 0) {
            return;
        }

        const newFiles = [...files];
        selectedFiles.forEach((f) => {
            const newFile = {
                id: 0,
                guid: uuidv4(),
                fileTypeId: fileType,
                fileName: f.name,
                fileToUpload: f,
            };

            newFiles.push(newFile);
        });

        set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
        onChange && onChange(newFiles);
    };

    const onFileSelectCancel = () => {
        set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
    };

    const stringSorter = (a, b) => {
        const nameA = a.toUpperCase(); // ignore upper and lowercase
        const nameB = b.toUpperCase(); // ignore upper and lowercase
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }

        // names must be equal
        return 0;
    };

    const onAddFile = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const options = {
            ownerId: null,
            isOpen: true,
            maxFiles: 5,
            fileTypeName: sortedFileTypeOptions.find((t) => t.id === fileType).name,
        };
        set_fileSelectOptions(options);
    };

    if (!files || !fileTypeOptions) {
        return null;
    }

    return (
        <div style={{ marginTop: '10px' }}>
            <h2 className='decorated'>{title}</h2>

            <DataGrid
                dataSource={files}
                showBorders={true}
                showRowLines={false}
                showColumnHeaders={true}
                rowAlternationEnabled={true}
                columnMinWidth={50}
                columnAutoWidth={false}
                noDataText='No Files found'
            >
                <Column
                    dataField='fileTypeId'
                    caption='Type'
                    cellRender={onFileTypeRender}
                    width={150}
                    alignment='left'
                />
                <Column
                    dataField='fileName'
                    caption='Name'
                />
                <Column
                    caption='Status'
                    cellRender={onStatusRender}
                    width={150}
                    alignment='left'
                />
                <Column
                    dataField='url'
                    caption=''
                    cellRender={onDownloadRender}
                    width={60}
                    alignment='center'
                />
                <Column
                    caption=''
                    cellRender={onDeleteRender}
                    width={60}
                    alignment='center'
                />
            </DataGrid>
            <div style={{ width: '100%', marginTop: '10px' }}>
                File Type:&nbsp;
                <select
                    value={fileType}
                    onChange={(e) => set_fileType(parseInt(e.target.value))}
                    style={{ width: '200px' }}
                >
                    {sortedFileTypeOptions
                        .filter((f) => f.name !== 'Appraisal')
                        .map((t, index) => {
                            return (
                                <option
                                    key={'file' + index}
                                    value={t.id}
                                >
                                    {t.name}
                                </option>
                            );
                        })}
                </select>
                <button
                    type='button'
                    onClick={(e) => onAddFile(e)}
                    className='button hollow'
                    title='Add file'
                    style={{ margin: '5px', padding: '6px' }}
                >
                    Add New File(s)
                </button>
            </div>
            <FileSelectDialog
                options={fileSelectOptions}
                onSubmit={onFileSelectSubmit}
                onCancel={onFileSelectCancel}
            />
        </div>
    );
};
